export const WS_ERROR = 'error occurred in websocket, closing websocket';

/**
 * The socket URL used to be concatenated into the message whole, so the same defect filed a fresh
 * issue per host, and the `?sessionId=…&token=…` query put signed JWTs in issue titles. Only the path
 * goes in the message; the host travels on a tag and the query is dropped. Doubled slashes come from a
 * `domain-url` override that keeps its trailing slash, so they say nothing about which endpoint failed.
 *
 * Lives in its own module because the websocket modules each spawn a ComlinkWorker at import time;
 * sharing this through any of them would start a second worker in the others.
 */
export function describeSocket(url: string) {
  try {
    const { host, pathname } = new URL(url);
    return { host: host + pathname, path: pathname.replace(/\/{2,}/g, '/') };
  } catch {
    return { host: url, path: url };
  }
}

/**
 * partysocket clones every event before dispatch with `new e.constructor(e.type, e)`, which assumes the
 * DOM `(type, init)` signature. Its own synthetic close event takes `(code, reason)`, so a close raised
 * by the library rather than the browser arrives with `code` set to the string 'close'.
 */
export const closeEventCode = (event?: { code?: unknown }) =>
  typeof event?.code === 'number' ? event.code : undefined;

export enum SocketDisconnect {
  /** the page is going away, which kills every socket and is not a fault */
  PageUnload = 'page-unload',
  /** the browser says there is no connection at all */
  Offline = 'offline',
  /** the upgrade was refused or never answered, so this socket never carried a frame */
  HandshakeFailed = 'handshake-failed',
  /** the backend closed deliberately and said why */
  ServerError = 'server-error',
  ServerGoingAway = 'server-going-away',
  /** the connection vanished with no close frame: a proxy reaping it, a suspend, a dropped link */
  AbnormalClose = 'abnormal-close',
  /** closed cleanly, so the error was not the close itself */
  CleanClose = 'clean-close',
  Unknown = 'unknown'
}

export type SocketState = {
  online: boolean;
  unloading: boolean;
  /** whether this client ever completed a handshake, across all its reconnect attempts */
  everOpened: boolean;
  /** code of the most recent close; absent on the very first connect attempt */
  closeCode?: number;
};

/**
 * The browser's websocket `error` event carries no code and no reason — the spec withholds them so a
 * page cannot probe cross-origin hosts. So "was this the backend or the network" has to be inferred
 * from the state around it: whether we are online, whether a handshake ever succeeded, and the code
 * on the close that came with it. Reported alongside those inputs so the call can be second-guessed.
 */
export function socketDisconnectReason({ online, unloading, everOpened, closeCode }: SocketState) {
  if (unloading) return SocketDisconnect.PageUnload;
  if (!online) return SocketDisconnect.Offline;
  if (!everOpened) return SocketDisconnect.HandshakeFailed;

  switch (closeCode) {
    case 1000:
    case 1005:
      return SocketDisconnect.CleanClose;
    case 1001:
      return SocketDisconnect.ServerGoingAway;
    case 1006:
      return SocketDisconnect.AbnormalClose;
    case 1011:
    case 1012:
    case 1013:
      return SocketDisconnect.ServerError;
    default:
      // 4000+ is the application range, so the backend chose to close and named its own reason
      return closeCode && closeCode >= 4000 ? SocketDisconnect.ServerError : SocketDisconnect.Unknown;
  }
}
